import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  CardHeader,
  Box,
  Chip,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import LogoCavca from 'assets/Branding/cavcaLogoV2White.png';
import { useStyles } from './styles';
import { getOffer } from 'services/QuotationService';
import { useParams } from 'react-router-dom';
import OfferError from './error/OfferError';
import InsuranceLogoV2 from 'components/QuotationResume/InsuranceLogo/InsuranceLogoV2';
import AnimateTabs from './coverage/AnimateTabs';

const generateUniqueId = (quote: any): string => {
  return `${quote.insurerKey}-${quote.title}-${quote.licenseVehicle}`;
};

// Función para formatear valores monetarios (solo cotizaciones manuales)
const formatCurrencyValueForDisplay = (value: string | number, isManual: boolean = false): string => {
  if (!value) return "N/A";
  
  const stringValue = value.toString();
  
  // Si no es manual y ya tiene formato, lo devolvemos como está
  if (!isManual && (stringValue.includes('.') || stringValue.includes(','))) {
    return stringValue;
  }
  
  // Si es manual o no tiene formato, lo formateamos
  const numericValue = typeof value === 'string' ? 
    parseFloat(value.replace(/[^0-9]/g, '')) : value;
  if (isNaN(numericValue)) return "N/A";
  return numericValue.toLocaleString('es-CO');
};

// Función para mapear nombres de aseguradoras del API a las keys del componente InsuranceLogoV2
const mapInsurerKeyToLogoKey = (insurerKey: string): string => {
  if (!insurerKey) return '';
  
  // Normalizar el nombre de la aseguradora (convertir a mayúsculas)
  const normalized = insurerKey.toUpperCase().trim();
  
  const mappings: { [key: string]: string } = {
    'AXA': 'AXA',
    'BOLIVAR': 'BOLIVAR', 
    'EQUIDAD': 'EQUIDAD',
    'HDI': 'HDI',        // Para "Hdi" → "HDI"
    'MAPFRE': 'MAPFRE',
    'SBS': 'SBS',
    'SURA': 'SURA',
    'LIBERTY': 'LIBERTY',
    'ZURICH': 'ZURICH',
    'PREVISORA': 'PREVISORA',
    'SOLIDARIA': 'SOLIDARIA',
    'SEGUROS_DEL_ESTADO': 'ESTADO',
    'ESTADO': 'ESTADO'
  };
  
  return mappings[normalized] || normalized;
};

interface CoverageItem {
  key: string;
  label: string;
  value: string;
  visible: boolean;
}

const Offer = () => {
  const classes = useStyles();
  const { id: uuid } = useParams<{ id: string }>();

  const [loading, setLoading] = useState<boolean>(true);
  const [selectedQuotes, setSelectedQuotes] = useState<any[]>([]);
  const [error, setError] = useState<boolean | null>(false);

  const getOffers = async (identificador: string) => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 3000));

      const response = await getOffer(identificador);
      const products = response?.offer?.products;
      const id = response?.offer?.id;

      if (id && products) {
        setSelectedQuotes(products);
        setError(false);
      } else {
        setError(true);
      }
    } catch (error) {
      console.error('Error al obtener la oferta:', error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getOffers(uuid);
  }, [uuid]);

  useEffect(() => {
    document.title = 'CAVCA - Cuadro Comparativo de Seguros';
  }, []);
  return loading ? (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      width="100vw"
      height="100vh"
      bgcolor="background.default">
      <Skeleton variant="text" width="80%" height={40} style={{ marginBottom: 20 }} />
      <Skeleton variant="rect" width="80%" height={200} style={{ marginBottom: 20 }} />
      <Skeleton variant="text" width="60%" height={30} style={{ marginBottom: 10 }} />
      <Skeleton variant="rect" width="80%" height={200} style={{ marginBottom: 20 }} />
      <Skeleton variant="text" width="40%" height={30} style={{ marginBottom: 10 }} />
      <Skeleton variant="circle" width={60} height={60} style={{ marginBottom: 20 }} />
      <Skeleton variant="text" width="50%" height={30} />
    </Box>
  ) : error ? (
    <OfferError />
  ) : (
    <div className={classes.pageContainer}>
      <header className={classes.header}>
        <img className={classes.logo} src={LogoCavca} alt="logo" />
        <Typography variant="h5" className={classes.headerText}>
          Cuadro comparativo / Esta cotización tiene una duración de 10 días
        </Typography>
      </header>

      <Typography variant="body1" align="center" className={classes.introText}>
        Las condiciones, valores y coberturas indicadas pueden variar según ajustes en tarifas,
        políticas de la aseguradora y cualquier cambio en la información proporcionada en la
        solicitud.
      </Typography>

      {/* Información del Asegurado */}
      <Card style={{ marginBottom: '20px', backgroundColor: '#F8F9FA' }}>
        <CardContent>
          <Typography
            variant="subtitle1"
            style={{ color: '#02CBF4', marginBottom: '15px', fontWeight: 'bold' }}>
            Información del Asegurado
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" style={{ color: '#666' }}>
                Nombre del Asegurado
              </Typography>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {selectedQuotes[0]?.firstName}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" style={{ color: '#666' }}>
                Placa del Vehículo
              </Typography>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {selectedQuotes[0]?.licenseVehicle}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" style={{ color: '#666' }}>
                Documento
              </Typography>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {selectedQuotes[0]?.nDocument
                  ? `${selectedQuotes[0].nDocument.toString().slice(0, 2)}${'*'.repeat(
                      selectedQuotes[0].nDocument.toString().length - 4
                    )}${selectedQuotes[0].nDocument.toString().slice(-2)}`
                  : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" style={{ color: '#666' }}>
                Ciudad de Circulación
              </Typography>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {selectedQuotes[0]?.cityCirculation?.[0]
                  ?.split('-')
                  .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" style={{ color: '#666' }}>
                Modelo
              </Typography>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {selectedQuotes[0]?.modelo}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" style={{ color: '#666' }}>
                Valor Asegurado
              </Typography>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                $
                {selectedQuotes[0]?.tVehicle === 'usado'
                  ? (parseInt(selectedQuotes[0]?.valueInsured) * 1000).toLocaleString('es-CO')
                  : parseInt(selectedQuotes[0]?.valueInsured).toLocaleString('es-CO')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" style={{ color: '#666' }}>
                Servicio
              </Typography>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {selectedQuotes[0]?.service?.charAt(0).toUpperCase() +
                  selectedQuotes[0]?.service?.slice(1)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" style={{ color: '#666' }}>
                Referencia
              </Typography>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {`${selectedQuotes[0]?.ref1} ${selectedQuotes[0]?.ref2} ${selectedQuotes[0]?.ref3}`}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {selectedQuotes.map((quote, index) => {
          const uniqueId = generateUniqueId(quote);

          return (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card className={classes.card}>
                <CardHeader
                  title="Seguro todo riesgo auto"
                  className={classes.cardHeader}
                  titleTypographyProps={{ variant: 'h6' }}
                />
                <CardContent style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <div className={classes.chipContainer}>
                    <Chip
                      label={quote?.segment?.segment || 'Sin Segmento'}
                      className={
                        quote?.segment?.segment === 'Premium'
                          ? classes.premiumChip
                          : quote?.segment?.segment === 'Basico'
                          ? classes.basicChip
                          : quote?.segment?.segment === 'Estandar'
                          ? classes.estandarChip
                          : classes.noClassChip
                      }
                    />
                  </div>
                  <Grid
                    container
                    alignItems="center"
                    justifyContent="space-evenly"
                    style={{ flexGrow: 1 }}>
                    <Grid item>
                      <InsuranceLogoV2 insurance={quote.isManualQuote ? mapInsurerKeyToLogoKey(quote.insurerKey) : quote.insurerKey} />
                    </Grid>
                    <Grid item>
                      <div>
                        <Typography align="center">
                          Producto: {quote.title?.split('-')[1] || quote.title}
                        </Typography>
                        <Typography align="center">Placa: {quote.licenseVehicle}</Typography>
                      </div>
                    </Grid>
                  </Grid>
                  <AnimateTabs
                    visible={
                      quote.segment?.extracontractualCivilLiability?.visible ||
                      quote.segment?.coverages?.visible ||
                      quote.segment?.assistances?.visible
                    }
                    items={[
                      ...(quote.segment?.extracontractualCivilLiability?.visible &&
                      Array.isArray(quote.segment?.extracontractualCivilLiability?.items)
                        ? quote.segment.extracontractualCivilLiability.items.map(
                            (item: CoverageItem) => ({
                              ...item,
                              key: 'extracontractualCivilLiability',
                            })
                          )
                        : []),
                      ...(quote.segment?.coverages?.visible &&
                      Array.isArray(quote.segment?.coverages?.items)
                        ? quote.segment.coverages.items.map((item: CoverageItem) => ({
                            ...item,
                            key: 'coverages',
                          }))
                        : []),
                      ...(quote.segment?.assistances?.visible &&
                      Array.isArray(quote.segment?.assistances?.items)
                        ? quote.segment.assistances.items.map((item: CoverageItem) => ({
                            ...item,
                            key: 'assistances',
                          }))
                        : []),
                      ...(quote.segment?.additionalAssistances?.visible &&
                      Array.isArray(quote.segment?.additionalAssistances?.items)
                        ? quote.segment.additionalAssistances.items.map((item: CoverageItem) => ({
                            ...item,
                            key: 'additionalAssistances',
                          }))
                        : []),
                    ]}
                  />
                  <Divider className={classes.divider} />

                  <Grid container alignItems="center" justifyContent="center">
                    <Grid item xs={5} style={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="primary" style={{ fontWeight: 'bold' }}>
                        Pago Contado
                      </Typography>
                      <Typography variant="body1">$ {formatCurrencyValueForDisplay(quote.value, quote.isManualQuote)}</Typography>
                    </Grid>

                    <Divider orientation="vertical" flexItem className={classes.verticalDivider} />

                    <Grid item xs={5} style={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="primary" style={{ fontWeight: 'bold' }}>
                        Pagos 11 Cuotas
                      </Typography>
                      <Typography variant="body1">$ {formatCurrencyValueForDisplay(quote.installmentPayment, quote.isManualQuote)}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </div>
  );
};

export default Offer;
